/**
 * Property publication status enum
 * Defines all possible statuses for property objects
 */
export enum PropertyStatus {
  PUBLISHED = "published",
  ARCHIVED = "archived",
  DRAFT = "draft",
  PENDING = "pending",
  REJECTED = "rejected",
  SOLD = "sold",
  RENTED = "rented",
  EXPIRED = "expired",
}

/**
 * Property visibility configuration based on user roles
 */
export interface PropertyVisibilityConfig {
  showArchived: boolean;
  showDrafts: boolean;
  showPending: boolean;
  showRejected: boolean;
  showExpired: boolean;
}

/**
 * User roles that can see different property statuses
 */
export enum UserRole {
  PUBLIC = "public",
  USER = "user",
  USER_REALTOR = "user_realtor",
  MAIN_REALTOR = "main_realtor",
  ADMIN = "admin",
  SUPER_ADMIN = "super_admin",
}

/**
 * Status visibility matrix based on user roles
 */
export const STATUS_VISIBILITY_MATRIX: Record<UserRole, PropertyVisibilityConfig> = {
  [UserRole.PUBLIC]: {
    showArchived: false,
    showDrafts: false,
    showPending: false,
    showRejected: false,
    showExpired: false,
  },
  [UserRole.USER]: {
    showArchived: false,
    showDrafts: false,
    showPending: false,
    showRejected: false,
    showExpired: false,
  },
  [UserRole.USER_REALTOR]: {
    showArchived: false, // Realtors don't see archived unless they're admin+
    showDrafts: true,
    showPending: true,
    showRejected: false,
    showExpired: true,
  },
  [UserRole.MAIN_REALTOR]: {
    showArchived: true, // Main realtors can see archived
    showDrafts: true,
    showPending: true,
    showRejected: true,
    showExpired: true,
  },
  [UserRole.ADMIN]: {
    showArchived: true,
    showDrafts: true,
    showPending: true,
    showRejected: true,
    showExpired: true,
  },
  [UserRole.SUPER_ADMIN]: {
    showArchived: true,
    showDrafts: true,
    showPending: true,
    showRejected: true,
    showExpired: true,
  },
};

/**
 * Check if a user role can see archived properties
 */
export function canSeeArchivedProperties(userRole: UserRole | string | null): boolean {
  if (!userRole) return false;
  
  const role = userRole as UserRole;
  const config = STATUS_VISIBILITY_MATRIX[role];
  
  return config?.showArchived || false;
}

/**
 * Get property statuses that should be excluded from search/listing based on user role
 */
export function getExcludedStatusesForUser(userRole: UserRole | string | null): PropertyStatus[] {
  if (!userRole) {
    // Public users - exclude everything except published
    return [
      PropertyStatus.ARCHIVED,
      PropertyStatus.DRAFT,
      PropertyStatus.PENDING,
      PropertyStatus.REJECTED,
      PropertyStatus.EXPIRED,
    ];
  }

  const role = userRole as UserRole;
  const config = STATUS_VISIBILITY_MATRIX[role];
  
  if (!config) {
    // Unknown role - treat as public
    return [
      PropertyStatus.ARCHIVED,
      PropertyStatus.DRAFT,
      PropertyStatus.PENDING,
      PropertyStatus.REJECTED,
      PropertyStatus.EXPIRED,
    ];
  }

  const excludedStatuses: PropertyStatus[] = [];

  if (!config.showArchived) {
    excludedStatuses.push(PropertyStatus.ARCHIVED);
  }
  if (!config.showDrafts) {
    excludedStatuses.push(PropertyStatus.DRAFT);
  }
  if (!config.showPending) {
    excludedStatuses.push(PropertyStatus.PENDING);
  }
  if (!config.showRejected) {
    excludedStatuses.push(PropertyStatus.REJECTED);
  }
  if (!config.showExpired) {
    excludedStatuses.push(PropertyStatus.EXPIRED);
  }

  return excludedStatuses;
}

/**
 * Check if a property should be visible to a user based on its status and user role
 */
export function isPropertyVisibleToUser(
  propertyStatus: PropertyStatus | string,
  userRole: UserRole | string | null
): boolean {
  const excludedStatuses = getExcludedStatusesForUser(userRole);
  return !excludedStatuses.includes(propertyStatus as PropertyStatus);
}

/**
 * Get human-readable status label
 */
export function getStatusLabel(status: PropertyStatus | string): string {
  switch (status) {
    case PropertyStatus.PUBLISHED:
      return "Published";
    case PropertyStatus.ARCHIVED:
      return "Archived";
    case PropertyStatus.DRAFT:
      return "Draft";
    case PropertyStatus.PENDING:
      return "Pending Review";
    case PropertyStatus.REJECTED:
      return "Rejected";
    case PropertyStatus.SOLD:
      return "Sold";
    case PropertyStatus.RENTED:
      return "Rented";
    case PropertyStatus.EXPIRED:
      return "Expired";
    default:
      return "Unknown";
  }
}

/**
 * Get status color theme
 */
export function getStatusColor(status: PropertyStatus | string): 
  "success" | "danger" | "warning" | "default" | "primary" | "secondary" {
  switch (status) {
    case PropertyStatus.PUBLISHED:
      return "success";
    case PropertyStatus.ARCHIVED:
      return "danger";
    case PropertyStatus.DRAFT:
      return "default";
    case PropertyStatus.PENDING:
      return "warning";
    case PropertyStatus.REJECTED:
      return "danger";
    case PropertyStatus.SOLD:
      return "primary";
    case PropertyStatus.RENTED:
      return "secondary";
    case PropertyStatus.EXPIRED:
      return "warning";
    default:
      return "default";
  }
}